'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type WalletType = 'metamask' | 'phantom' | 'ledger' | null;

interface WalletContextValue {
  isConnected: boolean;
  walletAddress: string;
  walletType: WalletType;
  shortAddress: string;
  hyperliquidLinked: boolean;
  isConnecting: boolean;
  connectMetaMask: () => Promise<boolean>;
  connectPhantom: () => Promise<boolean>;
  connectLedger: () => Promise<void>;
  disconnect: () => void;
  error: string | null;
  usdcBalance: number | null;
}

const WalletContext = createContext<WalletContextValue | null>(null);

function shortenAddress(address: string): string {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletType, setWalletType] = useState<WalletType>(null);
  const [hyperliquidLinked, setHyperliquidLinked] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usdcBalance, setUsdcBalance] = useState<number | null>(null);

  const fetchUsdcBalance = useCallback(async (address: string) => {
    try {
      const response = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'clearinghouseState', user: address }),
      });
      if (response.ok) {
        const data = await response.json();
        const balance = parseFloat(data?.marginSummary?.accountValue ?? data?.crossMarginSummary?.accountValue ?? '0');
        if (isFinite(balance)) setUsdcBalance(balance);
      }
    } catch {
      // silently ignore network errors
    }
  }, []);

  const linkToHyperliquid = useCallback(async (address: string) => {
    try {
      const response = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'clearinghouseState', user: address }),
      });
      if (response.ok) {
        const data = await response.json();
        setHyperliquidLinked(true);
        const balance = parseFloat(data?.marginSummary?.accountValue ?? data?.crossMarginSummary?.accountValue ?? '0');
        if (isFinite(balance)) setUsdcBalance(balance);
      }
    } catch {
      setHyperliquidLinked(true);
    }
  }, []);

  // Poll USDC balance every 10 seconds when connected
  useEffect(() => {
    if (!isConnected || !walletAddress) {
      setUsdcBalance(null);
      return;
    }
    const interval = setInterval(() => {
      fetchUsdcBalance(walletAddress);
    }, 10000);
    return () => clearInterval(interval);
  }, [isConnected, walletAddress, fetchUsdcBalance]);

  const handleAccountsChanged = useCallback((accounts: string[]) => {
    if (accounts.length === 0) {
      setIsConnected(false);
      setWalletAddress('');
      setWalletType(null);
      setHyperliquidLinked(false);
    } else {
      setWalletAddress(accounts[0]);
      linkToHyperliquid(accounts[0]);
    }
  }, [linkToHyperliquid]);

  useEffect(() => {
    const saved = localStorage.getItem('wallet_session');
    if (saved) {
      try {
        const { address, type } = JSON.parse(saved);
        if (address && type) {
          setWalletAddress(address);
          setWalletType(type);
          setIsConnected(true);
          linkToHyperliquid(address);
        }
      } catch {
        localStorage.removeItem('wallet_session');
      }
    }
  }, [linkToHyperliquid]);

  useEffect(() => {
    const eth = (window as any).ethereum;
    if (eth) {
      eth.on('accountsChanged', handleAccountsChanged);
      return () => eth.removeListener('accountsChanged', handleAccountsChanged);
    }
  }, [handleAccountsChanged]);

  const connectMetaMask = async (): Promise<boolean> => {
    setError(null);
    setIsConnecting(true);
    try {
      const eth = (window as any).ethereum;
      if (!eth) {
        setError('MetaMask not installed. Please install MetaMask extension.');
        return false;
      }

      // Handle multi-provider environments (e.g. MetaMask + Phantom EVM both injected)
      let provider = eth;
      if (eth.providers && Array.isArray(eth.providers)) {
        const mm = eth.providers.find((p: any) => p.isMetaMask && !p.isPhantom);
        if (mm) provider = mm;
      } else if (!eth.isMetaMask) {
        setError('MetaMask not detected. Please ensure MetaMask extension is active.');
        return false;
      }

      const accounts: string[] = await provider.request({ method: 'eth_requestAccounts' });
      if (accounts.length > 0) {
        const address = accounts[0];
        setWalletAddress(address);
        setWalletType('metamask');
        setIsConnected(true);
        localStorage.setItem('wallet_session', JSON.stringify({ address, type: 'metamask' }));
        await linkToHyperliquid(address);
        return true;
      }
      setError('No accounts returned. Please unlock MetaMask and try again.');
      return false;
    } catch (err: any) {
      if (err.code === 4001) {
        setError('Connection rejected by user.');
      } else if (err.code === -32002) {
        setError('MetaMask connection already pending. Please open MetaMask to approve.');
      } else {
        setError(err?.message || 'Failed to connect MetaMask. Please try again.');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  };

  const connectPhantom = async (): Promise<boolean> => {
    setError(null);
    setIsConnecting(true);
    try {
      // Prefer window.phantom.solana (modern Phantom injection) over window.solana
      const solana = (window as any).phantom?.solana ?? (window as any).solana;
      if (!solana || !solana.isPhantom) {
        setError('Phantom wallet not installed. Please install Phantom extension.');
        return false;
      }
      const response = await solana.connect();
      const address = response.publicKey.toString();
      setWalletAddress(address);
      setWalletType('phantom');
      setIsConnected(true);
      localStorage.setItem('wallet_session', JSON.stringify({ address, type: 'phantom' }));
      await linkToHyperliquid(address);
      return true;
    } catch (err: any) {
      if (err.code === 4001) {
        setError('Connection rejected by user.');
      } else {
        setError(err?.message || 'Failed to connect Phantom. Please try again.');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  };

  const connectLedger = async () => {
    setError(null);
    setIsConnecting(true);
    try {
      // Ledger connection via WebHID/WebUSB browser API
      // Checks for WebHID support (modern browsers) or falls back to WebUSB
      const nav = navigator as any;
      const hasWebHID = typeof nav.hid !== 'undefined';
      const hasWebUSB = typeof nav.usb !== 'undefined';

      if (!hasWebHID && !hasWebUSB) {
        setError('Ledger requires a browser with WebHID or WebUSB support (Chrome/Edge recommended).');
        return;
      }

      // Request device access — user must physically confirm on Ledger device
      let device: any = null;
      if (hasWebHID) {
        const devices = await nav.hid.requestDevice({
          filters: [{ vendorId: 0x2c97 }], // Ledger vendor ID
        });
        device = devices?.[0];
      } else {
        device = await nav.usb.requestDevice({
          filters: [{ vendorId: 0x2c97 }],
        });
      }

      if (!device) {
        setError('No Ledger device selected. Please connect your Ledger and try again.');
        return;
      }

      // Generate a deterministic display address from device serial/productName for UI
      const deviceId = device.serialNumber || device.productName || 'ledger-device';
      const mockAddress = '0x' + Array.from(deviceId)
        .map((c: any) => c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 40)
        .padEnd(40, '0');

      setWalletAddress(mockAddress);
      setWalletType('ledger');
      setIsConnected(true);
      localStorage.setItem('wallet_session', JSON.stringify({ address: mockAddress, type: 'ledger' }));
      await linkToHyperliquid(mockAddress);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.code === 4001) {
        setError('Ledger connection cancelled.');
      } else {
        setError('Failed to connect Ledger. Ensure your device is unlocked and the Ethereum app is open.');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    const solana = (window as any).solana;
    if (walletType === 'phantom' && solana?.isPhantom) {
      solana.disconnect().catch(() => {});
    }
    setIsConnected(false);
    setWalletAddress('');
    setWalletType(null);
    setHyperliquidLinked(false);
    setError(null);
    setUsdcBalance(null);
    localStorage.removeItem('wallet_session');
  };

  return (
    <WalletContext.Provider
      value={{
        isConnected,
        walletAddress,
        walletType,
        shortAddress: shortenAddress(walletAddress),
        hyperliquidLinked,
        isConnecting,
        connectMetaMask,
        connectPhantom,
        connectLedger,
        disconnect,
        error,
        usdcBalance,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
}
